import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart, Bar, ReferenceLine, Cell } from 'recharts';
import { TrendingUp, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PricePoint {
  date: string;
  close: number;
  sma2?: number;
  sma12?: number;
  sma50?: number;
  sma200?: number;
  bbUpper?: number;
  bbMiddle?: number;
  bbLower?: number;
  rsi?: number | null;
  macdLine?: number | null;
  signalLine?: number | null;
  macdHistogram?: number | null;
}

type RangeKey = '1d' | '1w' | '1m' | '3m' | '6m' | '1y';

const RANGE_CONFIG: Record<RangeKey, { label: string; months: number; interval: string }> = {
  '1d': { label: '1D', months: 0.035, interval: '5m' },
  '1w': { label: '1W', months: 0.25, interval: '15m' },
  '1m': { label: '1M', months: 1, interval: '1d' },
  '3m': { label: '3M', months: 3, interval: '1d' },
  '6m': { label: '6M', months: 6, interval: '1d' },
  '1y': { label: '1Y', months: 12, interval: '1d' },
};

interface PriceHistoryChartProps {
  ticker: string;
  currency?: string;
  defaultRange?: RangeKey;
}

const calculateSMA = (data: PricePoint[], period: number, key: 'sma2' | 'sma12' | 'sma50' | 'sma200') => {
  return data.map((point, idx, arr) => {
    if (idx < period - 1) return { ...point, [key]: null };
    const slice = arr.slice(Math.max(0, idx - period + 1), idx + 1);
    const sum = slice.reduce((acc, p) => acc + p.close, 0);
    return { ...point, [key]: sum / slice.length };
  });
};

const calculateBollingerBands = (data: PricePoint[], period = 20, stdDevMultiplier = 2) => {
  return data.map((point, idx, arr) => {
    if (idx < period - 1) return { ...point, bbUpper: null, bbMiddle: null, bbLower: null };
    const slice = arr.slice(idx - period + 1, idx + 1);
    const mean = slice.reduce((acc, p) => acc + p.close, 0) / period;
    const variance = slice.reduce((acc, p) => acc + Math.pow(p.close - mean, 2), 0) / period;
    const stdDev = Math.sqrt(variance);
    return {
      ...point,
      bbMiddle: mean,
      bbUpper: mean + stdDevMultiplier * stdDev,
      bbLower: mean - stdDevMultiplier * stdDev,
    };
  });
};

const calculateRSI = (data: PricePoint[], period = 14) => {
  const result: PricePoint[] = [];
  let avgGain = 0;
  let avgLoss = 0;

  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      result.push({ ...data[i], rsi: null });
      continue;
    }
    const change = data[i].close - data[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;

    if (i < period) {
      avgGain += gain / period;
      avgLoss += loss / period;
      result.push({ ...data[i], rsi: null });
    } else if (i === period) {
      avgGain += gain / period;
      avgLoss += loss / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      result.push({ ...data[i], rsi: 100 - 100 / (1 + rs) });
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      result.push({ ...data[i], rsi: 100 - 100 / (1 + rs) });
    }
  }
  return result;
};

const calculateEMA = (data: number[], period: number): (number | null)[] => {
  const result: (number | null)[] = [];
  const multiplier = 2 / (period + 1);
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push(null);
    } else if (i === period - 1) {
      const sum = data.slice(0, period).reduce((a, b) => a + b, 0);
      result.push(sum / period);
    } else {
      const prev = result[i - 1]!;
      result.push((data[i] - prev) * multiplier + prev);
    }
  }
  return result;
};

const calculateMACD = (data: PricePoint[], fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
  const closes = data.map(d => d.close);
  const emaFast = calculateEMA(closes, fastPeriod);
  const emaSlow = calculateEMA(closes, slowPeriod);

  const macdLineValues: (number | null)[] = emaFast.map((f, i) => {
    const s = emaSlow[i];
    return f !== null && s !== null ? f - s : null;
  });

  const validMacd = macdLineValues.filter((v): v is number => v !== null);
  const signalEma = calculateEMA(validMacd, signalPeriod);

  let signalIdx = 0;
  return data.map((point, i) => {
    const macd = macdLineValues[i];
    if (macd === null) {
      return { ...point, macdLine: null, signalLine: null, macdHistogram: null };
    }
    const signal = signalEma[signalIdx] ?? null;
    signalIdx++;
    const histogram = macd !== null && signal !== null ? macd - signal : null;
    return { ...point, macdLine: macd, signalLine: signal, macdHistogram: histogram };
  });
};

export function PriceHistoryChart({ ticker, currency, defaultRange = '6m' }: PriceHistoryChartProps) {
  const [data, setData] = useState<PricePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>(defaultRange);
  const [showBB, setShowBB] = useState(false);
  const [showRSI, setShowRSI] = useState(false);
  const [showMACD, setShowMACD] = useState(false);

  useEffect(() => {
    fetchPriceHistory();
  }, [ticker, range]);

  const fetchPriceHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const config = RANGE_CONFIG[range];
      const { data: historyData, error: fetchError } = await supabase.functions.invoke('fetch-price-history', {
        body: { ticker, currency, months: config.months, interval: config.interval }
      });

      if (fetchError) throw fetchError;

      if (!historyData?.prices || historyData.prices.length === 0) {
        throw new Error('No price history available');
      }

      let processedData = historyData.prices;
      if (processedData.length >= 2) processedData = calculateSMA(processedData, 2, 'sma2');
      if (processedData.length >= 12) processedData = calculateSMA(processedData, 12, 'sma12');
      if (processedData.length >= 50) processedData = calculateSMA(processedData, 50, 'sma50');
      if (processedData.length >= 200) processedData = calculateSMA(processedData, 200, 'sma200');
      if (processedData.length >= 20) processedData = calculateBollingerBands(processedData);
      if (processedData.length >= 15) processedData = calculateRSI(processedData);
      if (processedData.length >= 26) processedData = calculateMACD(processedData);

      setData(processedData);
    } catch (err) {
      console.error('Error fetching price history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load price history');
    } finally {
      setLoading(false);
    }
  };

  const rangeLabel = RANGE_CONFIG[range].label;
  const showSma50 = data.length >= 50;
  const showSma200 = data.length >= 200;
  const hasBB = data.length >= 20;
  const hasRSI = data.length >= 15;
  const hasMACD = data.length >= 26;

  const lastRsi = data.filter(d => d.rsi != null).slice(-1)[0]?.rsi;
  const rsiColor = lastRsi != null && lastRsi >= 70 ? '#ef4444' : lastRsi != null && lastRsi <= 30 ? '#10b981' : '#8b5cf6';

  const RangeToggle = () => (
    <div className="flex items-center gap-2 flex-wrap">
      {hasBB && (
        <div className="flex items-center gap-1.5 mr-1">
          <Switch id="bb-toggle" checked={showBB} onCheckedChange={setShowBB} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3" />
          <Label htmlFor="bb-toggle" className="text-[10px] font-medium text-muted-foreground cursor-pointer">BB</Label>
        </div>
      )}
      {hasRSI && (
        <div className="flex items-center gap-1.5 mr-1">
          <Switch id="rsi-toggle" checked={showRSI} onCheckedChange={setShowRSI} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3" />
          <Label htmlFor="rsi-toggle" className="text-[10px] font-medium text-muted-foreground cursor-pointer">RSI</Label>
        </div>
      )}
      {hasMACD && (
        <div className="flex items-center gap-1.5 mr-1">
          <Switch id="macd-toggle" checked={showMACD} onCheckedChange={setShowMACD} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3" />
          <Label htmlFor="macd-toggle" className="text-[10px] font-medium text-muted-foreground cursor-pointer">MACD</Label>
        </div>
      )}
      <div className="flex items-center gap-0.5 rounded-lg border p-0.5">
        {(Object.keys(RANGE_CONFIG) as RangeKey[]).map(key => (
          <Button
            key={key}
            variant={range === key ? 'default' : 'ghost'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setRange(key)}
            disabled={loading}
          >
            {RANGE_CONFIG[key].label}
          </Button>
        ))}
      </div>
    </div>
  );

  const formatXTick = (value: string) => {
    const date = new Date(value);
    if (range === '1d') return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    if (range === '1w') return date.toLocaleDateString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    if (range === '1m' || range === '3m') return `${date.getMonth() + 1}/${date.getDate()}`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const chartTitle = `${rangeLabel} Price History & SMAs${showBB ? ' + BB' : ''}${showRSI ? ' + RSI' : ''}${showMACD ? ' + MACD' : ''}`;

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--background))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  };

  const tooltipLabelFormatter = (label: string) => {
    const d = new Date(label);
    return range === '1d' || range === '1w' ? d.toLocaleString() : d.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Price History
            </CardTitle>
            <RangeToggle />
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Price History
            </CardTitle>
            <RangeToggle />
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {chartTitle}
          </CardTitle>
          <RangeToggle />
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatXTick} />
            <YAxis tick={{ fontSize: 12 }} domain={['auto', 'auto']} tickFormatter={(value) => value.toFixed(2)} />
            <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [value?.toFixed(2) ?? '-', name]} labelFormatter={tooltipLabelFormatter} />
            <Legend />

            {showBB && (
              <>
                <Area type="monotone" dataKey="bbUpper" stroke="none" fill="hsl(var(--primary))" fillOpacity={0.06} name="BB Upper" legendType="none" />
                <Area type="monotone" dataKey="bbLower" stroke="none" fill="hsl(var(--background))" fillOpacity={1} name="BB Lower" legendType="none" />
                <Line type="monotone" dataKey="bbUpper" stroke="#8b5cf6" strokeWidth={1} dot={false} name="BB Upper" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="bbMiddle" stroke="#8b5cf6" strokeWidth={1} dot={false} name="BB Mid (SMA20)" strokeDasharray="6 3" />
                <Line type="monotone" dataKey="bbLower" stroke="#8b5cf6" strokeWidth={1} dot={false} name="BB Lower" strokeDasharray="3 3" />
              </>
            )}

            <Line type="monotone" dataKey="close" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name="Price" />
            <Line type="monotone" dataKey="sma2" stroke="#10b981" strokeWidth={1.5} dot={false} name="SMA 2" strokeDasharray="5 5" />
            <Line type="monotone" dataKey="sma12" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="SMA 12" />
            {showSma50 && <Line type="monotone" dataKey="sma50" stroke="#f59e0b" strokeWidth={1.5} dot={false} name="SMA 50" />}
            {showSma200 && <Line type="monotone" dataKey="sma200" stroke="#ef4444" strokeWidth={1.5} dot={false} name="SMA 200" />}
          </ComposedChart>
        </ResponsiveContainer>

        {/* RSI Sub-Chart */}
        {showRSI && hasRSI && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-muted-foreground">RSI (14)</span>
              {lastRsi != null && (
                <span className={`text-xs font-bold ${lastRsi >= 70 ? 'text-destructive' : lastRsi <= 30 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                  {lastRsi.toFixed(1)} {lastRsi >= 70 ? '⬆ Overbought' : lastRsi <= 30 ? '⬇ Oversold' : ''}
                </span>
              )}
            </div>
            <ResponsiveContainer width="100%" height={120}>
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={formatXTick} hide />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} ticks={[0, 30, 50, 70, 100]} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [value?.toFixed(1) ?? '-', 'RSI']} labelFormatter={tooltipLabelFormatter} />
                <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" />
                <ReferenceLine y={50} stroke="hsl(var(--muted-foreground))" strokeDasharray="6 3" strokeOpacity={0.4} />
                <ReferenceLine y={30} stroke="#10b981" strokeDasharray="3 3" />
                <Area type="monotone" dataKey="rsi" stroke="none" fill="#ef4444" fillOpacity={0.05} baseValue={70} />
                <Area type="monotone" dataKey="rsi" stroke="none" fill="#10b981" fillOpacity={0.05} baseValue={30} />
                <Line type="monotone" dataKey="rsi" stroke={rsiColor} strokeWidth={1.5} dot={false} name="RSI" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* MACD Sub-Chart */}
        {showMACD && hasMACD && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-muted-foreground">MACD (12, 26, 9)</span>
              {(() => {
                const last = data.filter(d => d.macdHistogram != null).slice(-1)[0];
                if (!last) return null;
                const h = last.macdHistogram!;
                return (
                  <span className={`text-xs font-bold ${h >= 0 ? 'text-emerald-500' : 'text-destructive'}`}>
                    Histogram: {h >= 0 ? '+' : ''}{h.toFixed(4)}
                  </span>
                );
              })()}
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={formatXTick} hide />
                <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} tickFormatter={(v) => v.toFixed(3)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [value?.toFixed(4) ?? '-', name]} labelFormatter={tooltipLabelFormatter} />
                <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeOpacity={0.5} />
                <Bar dataKey="macdHistogram" name="Histogram" maxBarSize={3}>
                  {data.map((entry, index) => (
                    <Cell key={index} fill={entry.macdHistogram != null && entry.macdHistogram >= 0 ? '#10b981' : '#ef4444'} fillOpacity={0.7} />
                  ))}
                </Bar>
                <Line type="monotone" dataKey="macdLine" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="MACD" />
                <Line type="monotone" dataKey="signalLine" stroke="#f59e0b" strokeWidth={1.5} dot={false} name="Signal" strokeDasharray="4 2" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}